import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import {
  BookingError,
  confirmShareBooking,
  fetchSharePage,
} from '../services/interviewBookingApi';
import { ErrorPanel, MeetingDetails, NoticePanel } from './interviewBooking/BookingPanels';
import { DetailsForm } from './interviewBooking/DetailsForm';
import { SlotPicker } from './interviewBooking/SlotPicker';
import { groupByDay } from './interviewBooking/format';
import finnLogo from '../../../assets/finn-logo.png';

const errText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback);
const errStatus = (err: unknown) => (err instanceof BookingError ? err.status : 0);

/**
 * The LinkedIn link — `/book/s/:shareToken`, the page a prospect reaches from an InMail.
 *
 * The same slot picker as a personal invite, plus one question: their LinkedIn profile, because
 * they are usually not in Lever yet and that is what the recruiter will look for. Once booked, the
 * page MOVES to the prospect's own manage page (`/book/<their token>/manage`): from there on
 * everything — reschedule, cancel, the calendar file — is the invite path, under a token that is
 * theirs alone. The share token in this URL never manages anything.
 *
 * Public, outside the auth gate, like the invite page. Analytics follow that page's rule: funnel
 * shape only (`share_link: true` to tell the two apart), never the token or anything typed.
 */
export default function ShareBookingView() {
  const { shareToken = '' } = useParams<{ shareToken?: string }>();
  // A screening link from an InMail says which language the InMail was in (`?lang=de|en`).
  const [search] = useSearchParams();
  const lang = search.get('lang');
  // The InMail's own ref (`?r=`, outreach, Nov 2 2026): tells the proxy which InMail the booking came from.
  const ref = search.get('r');
  const navigate = useNavigate();

  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [candidateName, setCandidateName] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['interview-booking-share', shareToken, lang],
    queryFn: () => fetchSharePage(shareToken, lang),
    retry: (count, err) => !(err instanceof BookingError && err.status < 500) && count < 2,
  });

  const days = useMemo(() => groupByDay(data?.slots ?? []), [data?.slots]);
  const activeDay = days.find((d) => d.key === selectedDay) ?? days[0] ?? null;
  const selectedSlot = selectedStart ? (data?.slots.find((s) => s.start === selectedStart) ?? null) : null;
  // Unknown link, closed link or role, or today's bookings used up: final answers, no retry.
  const terminal = error instanceof BookingError && [403, 404, 410, 429].includes(error.status);

  async function handleConfirm() {
    if (!selectedSlot) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { manageToken } = await confirmShareBooking(shareToken, {
        start: selectedSlot.start,
        candidateName,
        candidateEmail,
        linkedinUrl,
        ref,
      }, lang);
      void logAnalyticsEvent('booking_confirmed', { share_link: true });
      // Replace, not push: Back must not land on a form that would book a second time.
      navigate(`/book/${encodeURIComponent(manageToken)}/manage`, { replace: true });
    } catch (err) {
      void logAnalyticsEvent('booking_confirm_failed', { share_link: true, status: errStatus(err) });
      setSubmitError(errText(err, 'Could not book this slot'));
      if (err instanceof BookingError && err.status === 409) {
        setSelectedStart(null);
        await refetch();
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto min-h-screen max-w-5xl px-4 py-10">
      <header className="mb-6">
        <img src={finnLogo} alt="FINN" className="mb-6 h-7 w-auto" />
        <h1 className="text-2xl font-semibold text-gray-900">Book your interview</h1>
        {terminal ? null : (
          <p className="mt-1 text-sm text-gray-500">
            Choose a time that suits you. You’ll get a confirmation by email.
          </p>
        )}
      </header>

      {isPending ? (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading availability…
        </div>
      ) : terminal ? (
        <NoticePanel message={errText(error, 'This link can no longer be used.')} />
      ) : error || !data ? (
        <ErrorPanel message={errText(error, 'Could not load this booking link')} onRetry={() => void refetch()} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="grid md:grid-cols-[260px_1fr]">
            <MeetingDetails page={data} selectedSlot={selectedSlot} booking={null} />
            <div className="p-5">
              {data.slotError ? (
                <ErrorPanel message="No times can be offered right now." onRetry={() => void refetch()} />
              ) : selectedSlot ? (
                <DetailsForm
                  slot={selectedSlot}
                  minutes={data.minutes}
                  candidateName={candidateName}
                  candidateEmail={candidateEmail}
                  onName={setCandidateName}
                  onEmail={setCandidateEmail}
                  linkedinUrl={linkedinUrl}
                  onLinkedinUrl={setLinkedinUrl}
                  submitting={submitting}
                  error={submitError}
                  onBack={() => {
                    setSelectedStart(null);
                    setSubmitError(null);
                  }}
                  onConfirm={() => void handleConfirm()}
                />
              ) : (
                <SlotPicker
                  days={days}
                  activeDay={activeDay}
                  onSelectDay={setSelectedDay}
                  onSelectSlot={(slot) => setSelectedStart(slot.start)}
                  onRefresh={() => void refetch()}
                  refreshing={isFetching}
                />
              )}
              {submitError && !selectedSlot ? (
                <ErrorPanel message={submitError} onRetry={() => void refetch()} />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
